import React, { useState, useEffect } from 'react';
import { CreditCard, Building2, TrendingUp, CheckCircle, Clock, XCircle, ArrowUpCircle, AlertCircle, Loader, Filter } from 'lucide-react';
import { companyService } from '../../../../services/companyService';
import { getAllUpgradeRequests, approveUpgradeRequest, rejectUpgradeRequest } from '../../../../services/api/upgradeRequestApi';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';

const PLAN_ORDER = { BASIC: 1, STANDARD: 2, PROFESSIONAL: 3, ENTERPRISE: 4 };

const StatusBadge = ({ status }) => {
    const cfg = {
        PENDING: { Icon: Clock, cls: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Pending' },
        APPROVED: { Icon: CheckCircle, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Approved' },
        REJECTED: { Icon: XCircle, cls: 'bg-rose-100 text-rose-800 border-rose-200', label: 'Rejected' },
    }[status] || { Icon: Clock, cls: 'bg-gray-100 text-gray-600 border-gray-200', label: status };
    const { Icon } = cfg;
    return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold rounded-full border shadow-sm ${cfg.cls}`}>
            <Icon size={12} /> {cfg.label}
        </span>
    );
};

const PlanCard = ({ title, count, colorClass, gradientClass, active }) => (
    <div className={`relative bg-white rounded-2xl p-5 border ${active ? 'border-2 shadow-md ' + colorClass : 'border-gray-200'} transition-all`}>
        {active && <div className="absolute top-0 left-0 w-full h-1 rounded-t-xl bg-gradient-to-r ${gradientClass}" />}
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{title}</p>
        <div className="flex items-end gap-2">
            <h3 className={`text-3xl font-black ${active ? 'text-gray-900' : 'text-gray-400'}`}>{count}</h3>
            <p className="text-xs text-gray-400 font-medium mb-1.5">companies</p>
        </div>
    </div>
);

const SubscriptionPlansView = () => {
    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [requests, setRequests] = useState([]);
    const [filter, setFilter] = useState('PENDING'); // PENDING, HISTORY

    // Approval state
    const [reviewingId, setReviewingId] = useState(null);
    const [reviewNotes, setReviewNotes] = useState('');
    const [reviewError, setReviewError] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setReviewError(null);
        try {
            const [companiesData, requestsData] = await Promise.all([
                companyService.getCompanies(),
                getAllUpgradeRequests(null)
            ]);
            const companiesList = Array.isArray(companiesData) 
                ? companiesData 
                : (companiesData?.content || companiesData?.data || []);
            setCompanies(companiesList);
            setRequests(requestsData || []);
        } catch (e) {
            console.error('Failed to load subscription data', e);
            setReviewError('Error loading dashboard data: ' + (e?.response?.data?.message || e.message));
            setCompanies([]);
            setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (reqPublicId) => {
        try {
            setReviewingId(reqPublicId);
            setReviewError(null);
            await approveUpgradeRequest(reqPublicId, reviewNotes);
            setReviewNotes('');
            await loadData();
        } catch (e) {
            setReviewError(e?.response?.data?.message || e.message || 'Approve failed.');
        } finally {
            setReviewingId(null);
        }
    };

    const handleReject = async (reqPublicId) => {
        try {
            setReviewingId(reqPublicId);
            setReviewError(null);
            await rejectUpgradeRequest(reqPublicId, reviewNotes);
            setReviewNotes('');
            await loadData();
        } catch (e) {
            setReviewError(e?.response?.data?.message || e.message || 'Reject failed.');
        } finally {
            setReviewingId(null);
        }
    };

    // Calculate metrics
    const activeCompanies = companies.filter(c => (c.status || '').toUpperCase() === 'ACTIVE');
    const plansCount = activeCompanies.reduce((acc, c) => {
        const p = (c.subscriptionPlan || 'UNKNOWN').toUpperCase();
        acc[p] = (acc[p] || 0) + 1;
        return acc;
    }, {});

    const pendingRequests = requests.filter(r => r.status === 'PENDING').sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const historyRequests = requests.filter(r => r.status !== 'PENDING').sort((a, b) => new Date(b.reviewedAt || b.createdAt) - new Date(a.reviewedAt || a.createdAt));

    const displayedRequests = filter === 'PENDING' ? pendingRequests : historyRequests;

    if (loading) return <div className="p-8 flex justify-center"><LoadingSpinner size="lg" message="Loading plans..." /></div>;

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50/50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-6 sticky top-0 z-10">
                <div className="flex items-center justify-between max-w-7xl mx-auto">
                    <div>
                        <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                            <CreditCard size={24} className="text-indigo-600" />
                            Subscription Plans
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">Monitor plan distribution and manage upgrade requests across all tenants.</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-8 space-y-8">
                {/* Metrics */}
                <div>
                    <h2 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <TrendingUp size={16} className="text-gray-400" /> ACTIVE PLAN DISTRIBUTION
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="bg-gradient-to-br from-indigo-900 to-indigo-700 rounded-2xl p-5 text-white shadow-md relative overflow-hidden">
                            <div className="absolute right-0 top-0 opacity-10 p-4"><Building2 size={64} /></div>
                            <p className="text-xs font-bold text-indigo-200 uppercase tracking-wider mb-1">Total Active</p>
                            <h3 className="text-3xl font-black">{activeCompanies.length}</h3>
                        </div>
                        <PlanCard title="Basic" count={plansCount['BASIC'] || 0} colorClass="border-slate-400" />
                        <PlanCard title="Standard" count={plansCount['STANDARD'] || 0} active colorClass="border-blue-500" gradientClass="from-blue-400 to-blue-600" />
                        <PlanCard title="Professional" count={plansCount['PROFESSIONAL'] || 0} active colorClass="border-purple-500" gradientClass="from-purple-400 to-purple-600" />
                        <PlanCard title="Enterprise" count={plansCount['ENTERPRISE'] || 0} active colorClass="border-amber-500" gradientClass="from-amber-400 to-amber-600" />
                    </div>
                </div>

                {/* Upgrade Requests Section */}
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                            <ArrowUpCircle size={18} className="text-indigo-600" />
                            Global Upgrade Requests
                            {pendingRequests.length > 0 && (
                                <span className="bg-rose-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold ml-2">
                                    {pendingRequests.length} Pending
                                </span>
                            )}
                        </h2>
                        <div className="flex bg-gray-200/50 rounded-lg p-1">
                            <button
                                onClick={() => setFilter('PENDING')}
                                className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${filter === 'PENDING' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Pending Review
                            </button>
                            <button
                                onClick={() => setFilter('HISTORY')}
                                className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${filter === 'HISTORY' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                History
                            </button>
                        </div>
                    </div>

                    <div className="p-6">
                        {reviewError && (
                            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-xl mb-6 text-sm font-medium">
                                <AlertCircle size={16} /> {reviewError}
                            </div>
                        )}

                        {displayedRequests.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <Clock size={24} className="text-gray-300" />
                                </div>
                                <p className="text-sm font-bold text-gray-500">No {filter.toLowerCase()} requests found.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {displayedRequests.map(r => (
                                    <div key={r.publicId} className={`border rounded-2xl p-5 ${r.status === 'PENDING' ? 'bg-amber-50/30 border-amber-200' : 'bg-white border-gray-100 hover:border-gray-300 transition-colors'}`}>
                                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="text-lg font-black text-gray-900">{r.tenantName || 'Unknown Company'}</h3>
                                                    <StatusBadge status={r.status} />
                                                </div>
                                                <p className="text-sm text-gray-800 font-medium mb-1">
                                                    Requested Upgrade: <span className="text-gray-500 line-through mr-1">{r.currentPlan}</span> → <span className="text-indigo-600 font-bold">{r.requestedPlan}</span>
                                                </p>
                                                <p className="text-xs text-gray-500 mb-2">
                                                    Submitted by <span className="font-semibold">{r.requestedByName}</span> on {new Date(r.createdAt).toLocaleDateString('en-AE', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                                {r.requesterNotes && (
                                                    <div className="bg-white border border-gray-100 rounded-lg p-3 text-sm text-gray-600 italic">
                                                        "{r.requesterNotes}"
                                                    </div>
                                                )}
                                                {r.status !== 'PENDING' && r.adminNotes && (
                                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700 mt-2">
                                                        <span className="font-bold text-xs text-gray-400 uppercase mr-2">Admin Notes:</span>
                                                        {r.adminNotes}
                                                    </div>
                                                )}
                                            </div>

                                            {r.status === 'PENDING' && (
                                                <div className="w-full md:w-72 shrink-0 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                                                    <p className="text-xs font-bold text-gray-500 mb-2">Admin Review</p>
                                                    <textarea
                                                        rows={2}
                                                        value={reviewNotes}
                                                        onChange={e => setReviewNotes(e.target.value)}
                                                        placeholder="Add optional notes for the tenant..."
                                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 resize-none mb-3"
                                                    />
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleReject(r.publicId)}
                                                            disabled={reviewingId === r.publicId}
                                                            className="flex-1 py-2 rounded-lg text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
                                                        >
                                                            Reject
                                                        </button>
                                                        <button
                                                            onClick={() => handleApprove(r.publicId)}
                                                            disabled={reviewingId === r.publicId}
                                                            className="flex-1 py-2 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-50 flexItems-center justify-center"
                                                        >
                                                            {reviewingId === r.publicId ? <Loader size={14} className="animate-spin text-center w-full" /> : 'Approve'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionPlansView;
